import { Zap, GitPullRequest, Brain, CheckCircle, Code, Sparkles } from "lucide-react";

const features = [
  {
    icon: Brain,
    title: "AI Analysis",
    description: "Advanced ML models analyze errors with session replays and stack traces"
  },
  {
    icon: Code,
    title: "Smart Code Generation",
    description: "Generates production-ready fixes using GPT5 and Claude 4.5"
  },
  {
    icon: CheckCircle,
    title: "Automated Testing",
    description: "Validates fixes with test cases before creating pull requests"
  },
  {
    icon: GitPullRequest,
    title: "GitHub Integration",
    description: "Automatically opens PRs with detailed explanations for review"
  },
  {
    icon: Sparkles,
    title: "Slack Notifications",
    description: "Team gets notified instantly with one-click approval workflow"
  },
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "From error detection to deployed fix in under 15 minutes"
  }
];

export const SolutionSection = () => {
  return (
    <section className="py-16 sm:py-24 px-4 bg-background border-t border-border">
      <div className="container mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 tracking-tight">
            Meet Your New <span className="metallic-text">L3 Engineer</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4 font-light">
            The last generation to fight production fires alone. Self-healing systems are your new teammate.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto mb-16 sm:mb-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index}
                className="p-6 sm:p-8 bg-background border border-border rounded-xl hover:shadow-md transition-shadow duration-200"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-foreground/60" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 font-heading">{feature.title}</h3>
                <p className="text-sm sm:text-base text-muted-foreground font-light">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* How It Works Today vs. Tomorrow */}
        <div className="max-w-5xl mx-auto">
          <h3 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 tracking-tight">How It Works Today vs. Tomorrow</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* TODAY Column */}
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-destructive/10 border-b border-border p-4 sm:p-6">
                <h4 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Today : You Fix Production Bugs</h4>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 bg-background">
                <div className="flex items-start gap-3">
                  <span className="text-destructive text-lg">🔴</span>
                  <div>
                    <p className="font-semibold text-foreground">2:47 AM</p>
                    <p className="text-sm text-muted-foreground">Sentry incident alert</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Senior engineer woken up</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Opens laptop, reviews logs</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Searches codebase for error location</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Reproduces bug locally</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Does RCA and creates a fix</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">Validates and create PR</p>
                <div className="pt-4 border-t border-border mt-4">
                  <p className="text-sm sm:text-base text-foreground/90 pl-8">8:30 AM: Engineer reviews & merges</p>
                  <div className="mt-4 space-y-2 pl-8">
                    <p className="font-semibold text-foreground">⏱️ Total: 4 hours</p>
                    <p className="font-semibold text-foreground">💰 Cost: $200</p>
                    <p className="font-semibold text-destructive">😴 Engineer burned out</p>
                  </div>
                </div>
              </div>
            </div>

            {/* TOMORROW Column */}
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/10 border-b border-border p-4 sm:p-6">
                <h4 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Tomorrow: You Only Review AI Fix</h4>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 bg-background">
                <div className="flex items-start gap-3">
                  <span className="text-primary text-lg">✅</span>
                  <div>
                    <p className="font-semibold text-foreground">2:47 AM</p>
                    <p className="text-sm text-muted-foreground">Sentry incident alert</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">AI agent analyzes in 30 seconds</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">AI correlates: error + session replay + code</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">AI generates fix with test coverage</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">AI validates fix</p>
                <p className="text-sm sm:text-base text-foreground/90 pl-8">AI opens PR with context</p>
                <div className="pt-4 border-t border-border mt-4">
                  <p className="text-sm sm:text-base text-foreground/90 pl-8">8:30 AM: Engineer reviews & merges</p>
                  <div className="mt-4 space-y-2 pl-8">
                    <p className="font-semibold text-foreground">⏱️ Total: 15 minutes</p>
                    <p className="font-semibold text-foreground">💰 Cost: $12</p>
                    <p className="font-semibold text-primary">😴 Engineer well-rested</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};